import api from './api';
import type { PublicRecipientProfilePayload } from './parishService';

interface DashboardStats {
  totalEvents: number;
  activeEvents: number;
  totalRegistrations: number;
  totalRevenue: number;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export interface PaymentAttempt {
  id: string;
  provider?: string | null;
  providerPaymentId: string;
  paymentMethod?: string | null;
  status: string;
  amount?: number | null;
  errorMessage?: string | null;
  createdAt: string;
}

export interface PendingRefundRequest {
  id: string;
  amount: number;
  reason: string;
  requestedAt: string;
  status: string;
}

export interface Registration {
  id: string;
  eventId?: string | null;
  confirmationCode: string;
  status: string;
  paymentStatus: string;
  paymentMethod?: string | null; // Método de pagamento (PIX, Cartão de Crédito, etc)
  eventTitle: string;
  ticketName: string;
  participantName: string;
  participantEmail: string;
  formData: Record<string, any>; // Todos os dados do formulário
  amountPaid: number;
  parishFee?: number | null;
  feePercentage?: number | null;
  createdAt: string;
  checkedIn?: boolean;
  checkedInAt?: string | null;
  orderId?: string | null;
  orderAttendeeCount?: number;
  /** Pessoas nesta inscrição (grupo). Padrão 1. */
  quantity?: number;
  paymentAttemptCount?: number;
  failedPaymentAttemptCount?: number;
  pendingRefundRequest?: PendingRefundRequest | null;
  /** Presente quando o evento já teve saque (concluído ou em andamento). */
  refundBlocked?: { code: string; message: string } | null;
}

export interface WithdrawalDataResponse {
  hasWithdrawalData: boolean;
  pixKeyMasked?: string;
  pixKeyType?: 'cpf' | 'cnpj' | 'email' | 'phone' | 'random';
  pagarmeRecipientReady?: boolean;
  recipientOnboardingStatus?: string;
  recipientPendencies?: string[];
}

export interface PagarmeRecipientProfilePayload {
  registerType?: 'individual' | 'corporation';
  individual?: {
    motherName?: string;
    birthdate?: string;
    monthlyIncome?: string;
    professionalOccupation?: string;
  };
  corporation?: {
    companyName?: string;
    tradingName?: string;
    annualRevenue?: string;
    foundingDate?: string;
    representative?: {
      name?: string;
      email?: string;
      document?: string;
      birthdate?: string;
      phone?: string;
      monthlyIncome?: string;
      professionalOccupation?: string;
    };
  };
  bankAccount?: {
    holderName?: string;
    holderDocument?: string;
    bank?: string;
    branchNumber?: string;
    branchCheckDigit?: string;
    accountNumber?: string;
    accountCheckDigit?: string;
    type?: 'checking' | 'savings';
  };
  addressOverride?: {
    street?: string;
    streetNumber?: string;
    complementary?: string;
    neighborhood?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    referencePoint?: string;
  };
}

export interface WithdrawalEvent {
  eventId: string;
  title: string;
  slug: string;
  endDate?: string;
  totalParishFee: number;
  alreadyWithdrawn: number;
  availableBalance: number;
  totalRegistrations: number;
  /** Saque solicitável por aqui. Se false, repasse é manual pela plataforma. */
  selfServiceAvailable?: boolean;
  /** Canal preferencial do repasse: Pagarme (checkout split) ou Mercado Pago (legado). */
  settlementChannel?: 'pagarme' | 'mercadopago';
}

export interface PendingWithdrawal {
  eventId: string;
  eventTitle: string;
  amount: number;
  status: string;
  requestedAt: string;
}

export interface WithdrawalRequestItem {
  id: string;
  eventId: string;
  eventTitle: string;
  amount: number;
  status: string;
  source?: 'parish_request' | 'admin_manual';
  requestedAt: string;
  completedAt?: string;
  rejectionReason?: string;
}

export interface WithheldWithdrawalBalance {
  eventId: string;
  title: string;
  accumulatedBalance: number;
  endDate: string;
  reason: 'event_not_ended';
}

export interface WithdrawalEventsResponse {
  eventsWithBalance: WithdrawalEvent[];
  /** Saldo acumulado mas ainda não liberado para saque (ex.: evento não encerrou). */
  withheldBalances?: WithheldWithdrawalBalance[];
  pendingWithdrawal: PendingWithdrawal | null;
  /** Saque em andamento em outro evento da paróquia (informativo; trava é por evento). */
  parishPendingWithdrawal?: PendingWithdrawal | null;
  requested: WithdrawalRequestItem[];
  completed: WithdrawalRequestItem[];
  withdrawalGlobalState?: {
    enabled: boolean;
    suspended: boolean;
    message?: string;
  };
}

export const withdrawalService = {
  getWithdrawalData: async (): Promise<WithdrawalDataResponse> => {
    const response = await api.get<ApiResponse<WithdrawalDataResponse>>('/dashboard/withdrawal-data');
    return response.data.data;
  },
  saveWithdrawalData: async (data: { pixKeyType: 'cpf' | 'cnpj' | 'email' | 'phone' | 'random'; pixKey: string }): Promise<void> => {
    const response = await api.put<ApiResponse<null>>('/dashboard/withdrawal-data', data);
    if (!response.data.success) throw new Error(response.data.message || 'Erro ao salvar');
  },
  getWithdrawalEvents: async (eventId?: string): Promise<WithdrawalEventsResponse> => {
    const response = await api.get<ApiResponse<WithdrawalEventsResponse>>('/dashboard/withdrawal/events', {
      params: eventId ? { eventId } : undefined,
    });
    return response.data.data;
  },
  sendWithdrawalToken: async (eventId: string, amount: number): Promise<void> => {
    const response = await api.post<ApiResponse<{ message: string }>>('/dashboard/withdrawal/send-token', {
      eventId,
      amount,
    });
    if (!response.data.success) throw new Error(response.data.message || 'Erro ao enviar código');
  },
  createWithdrawalRequest: async (
    eventId: string,
    token: string,
    amount: number
  ): Promise<{ amount: number; eventTitle: string }> => {
    const response = await api.post<ApiResponse<{ amount: number; eventTitle: string }>>(
      '/dashboard/withdrawal/request',
      { eventId, token, amount }
    );
    return response.data.data;
  },
  cancelWithdrawalRequest: async (requestId: string): Promise<void> => {
    const response = await api.delete<ApiResponse<null>>(`/dashboard/withdrawal/request/${requestId}`);
    if (!response.data.success) throw new Error(response.data.message || 'Erro ao cancelar');
  },

  getPagarmeRecipientProfile: async (): Promise<Record<string, unknown>> => {
    const response = await api.get<ApiResponse<Record<string, unknown>>>('/dashboard/pagarme-recipient');
    return response.data.data;
  },

  savePagarmeRecipientProfile: async (payload: PagarmeRecipientProfilePayload): Promise<Record<string, unknown>> => {
    const response = await api.put<ApiResponse<{ recipientProfile?: unknown; pendencies?: string[] }>>(
      '/dashboard/pagarme-recipient',
      payload
    );
    return response.data as unknown as Record<string, unknown>;
  },

  syncPagarmeRecipient: async (opts?: { dryRun?: boolean }): Promise<Record<string, unknown>> => {
    const response = await api.post<ApiResponse<Record<string, unknown>>>(
      '/dashboard/pagarme-recipient/sync',
      opts || {}
    );
    return response.data as unknown as Record<string, unknown>;
  },
};

export const dashboardService = {
  getParishDashboardSummary: async (): Promise<DashboardStats> => {
    const response = await api.get<ApiResponse<DashboardStats>>('/dashboard/summary');
    const data = response.data?.data;
    return {
      totalEvents: Number(data?.totalEvents) || 0,
      activeEvents: Number(data?.activeEvents) || 0,
      totalRegistrations: Number(data?.totalRegistrations) || 0,
      totalRevenue: Number(data?.totalRevenue) || 0,
    };
  },

  createAdditionalOrganization: async (payload: {
    phone?: string;
    parishData: Record<string, unknown>;
    recipientProfile: PublicRecipientProfilePayload;
  }) => {
    const response = await api.post('/dashboard/organizations', payload);
    return response.data;
  },
};

export const registrationService = {
  getAll: async (): Promise<Registration[]> => {
    const response = await api.get<ApiResponse<Registration[]>>('/dashboard/registration', {
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
        'Expires': '0'
      }
    });
      console.log('📦 [registrationService.getAll] Response:', response.data);

    return Array.isArray(response.data?.data) ? response.data.data : [];
  },

  getByEvent: async (eventId: string): Promise<Registration[]> => {
    const response = await api.get<ApiResponse<Registration[]>>('/dashboard/registration', {
      params: { eventId },
      headers: {
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
        'Expires': '0',
      },
    });
    return response.data.data;
  },

  getPaymentAttempts: async (registrationId: string): Promise<PaymentAttempt[]> => {
    const response = await api.get<ApiResponse<PaymentAttempt[]>>(
      `/dashboard/registration/${registrationId}/attempts`
    );
    return response.data.data || [];
  },

  getDataChanges: async (
    registrationId: string
  ): Promise<
    Array<{
      id: string;
      changes: Array<{ field: 'name' | 'email' | 'phone'; from: string; to: string }>;
      note?: string | null;
      changedByName?: string | null;
      changedByEmail?: string | null;
      createdAt: string;
    }>
  > => {
    const response = await api.get<ApiResponse<any[]>>(
      `/dashboard/registration/${registrationId}/data-changes`
    );
    return response.data.data || [];
  },

  updateParticipant: async (
    registrationId: string,
    payload: { name?: string; email?: string; phone?: string; note?: string }
  ): Promise<{
    success: boolean;
    message?: string;
    data?: {
      participantName: string;
      participantEmail: string;
      formData: Record<string, any>;
      changes?: Array<{ field: string; from: string; to: string }>;
    };
  }> => {
    const response = await api.patch(`/dashboard/registration/${registrationId}/participant`, payload);
    return response.data;
  },

  requestRefund: async (
    registrationId: string,
    payload: { reason: string; password: string; amount?: number }
  ): Promise<{ success: boolean; message?: string; data?: unknown }> => {
    const response = await api.post(`/dashboard/registration/${registrationId}/refund-request`, payload);
    return response.data;
  },

  cancelRefundRequest: async (
    registrationId: string
  ): Promise<{ success: boolean; message?: string }> => {
    const response = await api.delete(`/dashboard/registration/${registrationId}/refund-request`);
    return response.data;
  },

  exportEventRegistrations: async (eventId: string): Promise<void> => {
    try {
      const response = await api.get(`/events/${eventId}/export-registrations`, {
        responseType: 'blob',
        headers: {
          'Accept': 'text/csv',
        }
      });

      // Criar link para download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      
      // Extrair nome do arquivo do header Content-Disposition
      const contentDisposition = response.headers['content-disposition'];
      const dateStamp = new Date().toISOString().slice(0, 10);
      let filename = `inscricoes_evento_${dateStamp}.csv`;
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao exportar inscrições:', error);
      throw new Error('Falha ao exportar inscrições do evento');
    }
  },

  exportEventRegistrationsXlsx: async (eventId: string): Promise<void> => {
    try {
      const response = await api.get(`/events/${eventId}/export-registrations-xlsx`, {
        responseType: 'blob',
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      const contentDisposition = response.headers['content-disposition'];
      const dateStamp = new Date().toISOString().slice(0, 10);
      let filename = `inscricoes_evento_${dateStamp}.xlsx`;

      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erro ao exportar inscrições XLSX:', error);
      throw new Error('Falha ao exportar inscrições do evento');
    }
  }
};

export type CancellationTicketRow = {
  _id: string;
  email: string;
  reason: string;
  status: string;
  eventTitle?: string | null;
  verifiedAt?: string | null;
  handledAt?: string | null;
  parishDecision?: 'pending' | 'approved' | 'rejected' | null;
  parishRespondedAt?: string | null;
  parishResponseNote?: string | null;
  eligibilityBucket?: string | null;
  eligibilityMessage?: string | null;
  registrationId?: string | null;
  overdueNotifiedAt?: string | null;
};

export const cancellationTicketService = {
  listByEvent: async (
    eventId: string,
    status: 'opened' | 'handled' | 'all' = 'all'
  ): Promise<{ tickets: CancellationTicketRow[]; pendingCount: number }> => {
    const response = await api.get<
      ApiResponse<{ tickets: CancellationTicketRow[]; pendingCount: number }>
    >(`/dashboard/events/${eventId}/cancellation-tickets`, { params: { status } });
    return response.data.data || { tickets: [], pendingCount: 0 };
  },

  approve: async (eventId: string, ticketId: string, note?: string) => {
    const response = await api.post(
      `/dashboard/events/${eventId}/cancellation-tickets/${ticketId}/approve`,
      { note }
    );
    return response.data;
  },

  reject: async (eventId: string, ticketId: string, note?: string) => {
    const response = await api.post(
      `/dashboard/events/${eventId}/cancellation-tickets/${ticketId}/reject`,
      { note }
    );
    return response.data;
  },
};